import { useLayoutEffect, useState } from 'react';

type Breakpoint = {
  readonly columns: number;
  readonly minWidth: number;
};

function getColumnsForWidth(
  availableWidth: number,
  breakpoints: readonly Breakpoint[],
): number {
  // breakpoints must be sorted descending by minWidth for this to work
  const match = breakpoints
    .slice()
    .sort((a, b) => b.minWidth - a.minWidth)
    .find((bp) => availableWidth >= bp.minWidth);

  return match?.columns ?? breakpoints.at(-1)?.columns ?? 1;
}

/**
 * Tracks an element's width via ResizeObserver and maps it to a column count
 * using the provided breakpoints.
 *
 * The element is taken as a value (not a ref) so the hook can re-attach its
 * observer when the element identity changes — e.g. when the host component
 * conditionally unmounts and remounts its scroll container. Pair with a
 * callback ref that stores the element in state.
 *
 * Breakpoints do not need to be pre-sorted - the hook sorts them internally.
 * Uses `useLayoutEffect` to measure before paint, preventing a flash of the
 * wrong column count on initial render.
 *
 * @example
 * const BREAKPOINTS = [
 *   { minWidth: 1280, columns: 4 },
 *   { minWidth: 1024, columns: 3 },
 *   { minWidth: 768, columns: 2 },
 *   { minWidth: 0, columns: 1 },
 * ] as const;
 *
 * const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
 * const columns = useResponsiveColumns(scrollEl, BREAKPOINTS);
 * return <div ref={setScrollEl} />;
 */
export function useResponsiveColumns(
  element: HTMLElement | null,
  breakpoints: readonly Breakpoint[],
): number {
  const [columns, setColumns] = useState(() => {
    // Before we have an element, default to the largest breakpoint so the
    // first paint doesn't flash with a single-column fallback.
    const sorted = breakpoints.slice().sort((a, b) => b.minWidth - a.minWidth);

    return sorted[0]?.columns ?? 1;
  });

  useLayoutEffect(() => {
    if (!element) {
      return;
    }

    const update = () => {
      const nextColumns = getColumnsForWidth(element.clientWidth, breakpoints);

      setColumns((prev) => (prev === nextColumns ? prev : nextColumns));
    };

    // Measure immediately so the column count is right on first paint and
    // not only after a subsequent resize event.
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, breakpoints]);

  return columns;
}
